import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Logo } from "@/components/Navbar";
import NotificationBell from "@/components/NotificationBell";
import { LayoutDashboard, CreditCard, Shield, LogOut, Menu, X, Trophy, User, Sparkles, Bookmark, CalendarDays, MessageCircle, Wallet, Megaphone, Rocket, Users, Clapperboard } from "lucide-react";

const NavItem = ({ to, icon: Icon, label, onClick, badge }) => (
  <NavLink to={to} onClick={onClick} data-testid={`sidebar-${label.toLowerCase().replace(/\s/g, "-")}`}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
        isActive ? "bg-primary/10 text-primary border border-primary/20" : "text-muted-foreground hover:text-foreground hover:bg-white/5"
      }`}>
    <Icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} /> <span className="flex-1">{label}</span>
    {badge > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-secondary text-white text-[11px] font-bold flex items-center justify-center" data-testid="messages-unread-badge">{badge > 9 ? "9+" : badge}</span>}
  </NavLink>
);

const DashboardShell = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const prevUnread = React.useRef(null);

  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch { /* ignore */ }
    }
  }, []);

  useEffect(() => {
    let alive = true;
    const fetchUnread = () => api.get("/support/unread-count").then((r) => alive && setUnread(r.data.unread)).catch(() => {});
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [location.pathname]);

  // Desktop notification when the unread count increases (a new support reply arrived).
  useEffect(() => {
    const prev = prevUnread.current;
    if (prev !== null && unread > prev && typeof Notification !== "undefined" && Notification.permission === "granted") {
      try {
        const n = new Notification("💬 New CloutRise reply", { body: "You have a new reply on your support conversation.", tag: "cloutrise-support" });
        n.onclick = () => { window.focus(); navigate("/messages"); n.close(); };
      } catch { /* ignore */ }
    }
    prevUnread.current = unread;
  }, [unread, navigate]);

  // Flash the browser tab title when there's an unread support reply and the tab isn't focused.
  useEffect(() => {
    const base = "CloutRise";
    if (!unread || unread <= 0) { document.title = base; return; }
    const alt = `💬 (${unread}) New reply!`;
    let on = false;
    const tick = () => {
      if (!document.hidden) { document.title = base; return; }
      on = !on;
      document.title = on ? alt : base;
    };
    const iv = setInterval(tick, 1000);
    const onFocus = () => { document.title = base; };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onFocus); document.title = base; };
  }, [unread]);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-6 flex items-center justify-between"><Logo /><NotificationBell /></div>
      <nav className="flex-1 px-4 space-y-1.5">
        <NavItem to="/dashboard" icon={LayoutDashboard} label="Overview" onClick={() => setOpen(false)} />
        <NavItem to="/studio" icon={Sparkles} label="AI Studio" onClick={() => setOpen(false)} />
        <NavItem to="/grow" icon={Rocket} label="Viral Lab" onClick={() => setOpen(false)} />
        <NavItem to="/video-studio" icon={Clapperboard} label="Video Studio" onClick={() => setOpen(false)} />
        <NavItem to="/collab" icon={Users} label="Collab Board" onClick={() => setOpen(false)} />
        <NavItem to="/amplify" icon={Megaphone} label="Amplify" onClick={() => setOpen(false)} />
        <NavItem to="/calendar" icon={CalendarDays} label="Calendar" onClick={() => setOpen(false)} />
        <NavItem to="/library" icon={Bookmark} label="Library" onClick={() => setOpen(false)} />
        <NavItem to="/leaderboard" icon={Trophy} label="Leaderboard" onClick={() => setOpen(false)} />
        <NavItem to="/profile" icon={User} label="Profile" onClick={() => setOpen(false)} />
        <NavItem to="/messages" icon={MessageCircle} label="My Messages" onClick={() => setOpen(false)} badge={unread} />
        <NavItem to="/plans" icon={CreditCard} label="Plans" onClick={() => setOpen(false)} />
        <NavItem to="/billing" icon={Wallet} label="Billing" onClick={() => setOpen(false)} />
        {user?.role === "admin" && <NavItem to="/admin" icon={Shield} label="Admin" onClick={() => setOpen(false)} />}
      </nav>
      <div className="p-4 border-t border-border/50">
        <div className="flex items-center gap-3 px-2 py-3">
          <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center text-primary font-bold text-sm shrink-0">
            {user?.picture ? <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover" /> : (user?.name?.[0] || user?.email?.[0] || "U").toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{user?.name || "Creator"}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
        </div>
        <button onClick={handleLogout} data-testid="logout-btn"
          className="w-full mt-2 flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors">
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <aside className="hidden md:block w-64 border-r border-border/50 fixed inset-y-0 left-0 bg-background">
        <SidebarContent />
      </aside>

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 h-16 border-b border-border/50 bg-background/80 backdrop-blur-xl flex items-center justify-between px-4">
        <Logo />
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu /></button>
      </div>
      {open && (
        <div className="md:hidden fixed inset-0 z-50 bg-background">
          <button onClick={() => setOpen(false)} className="absolute top-5 right-5 z-10" data-testid="mobile-menu-close"><X /></button>
          <SidebarContent />
        </div>
      )}

      <main className="flex-1 md:ml-64 pt-16 md:pt-0 min-w-0">{children}</main>
    </div>
  );
};

export default DashboardShell;
